<script setup lang="ts">
  import { computed } from 'vue';
  import { DIRECTION_TITLES } from './lib';
  import {
    type TransferModelValue,
    Direction,
    type Point,
    type Type,
    type Passengers,
    type Option,
    type DirectionModelValue,
  } from './Transfer.types';
  import DirectionSkeleton from './TransferDirection/DirectionSkeleton.vue';
  import TransferDirection from './TransferDirection/TransferDirection.vue';
  import ResultSkeleton from './TransferResult/ResultSkeleton.vue';
  import TransferResult from './TransferResult/TransferResult.vue';

  interface TransferProps {
    modelValue: TransferModelValue;
    passengers: Passengers;
    points: {
      [Direction.There]: Point[];
      [Direction.Back]: Point[];
    };
    types: {
      [Direction.There]: Type[];
      [Direction.Back]: Type[];
    };
    options: {
      [Direction.There]: Option[];
      [Direction.Back]: Option[];
    };
    loading?: boolean;
  }

  interface TransferEmits {
    (e: 'update:modelValue', value: TransferModelValue): void;
  }

  const props = withDefaults(defineProps<TransferProps>(), {
    loading: false,
  });

  const emit = defineEmits<TransferEmits>();

  const innerValue = computed({
    get() {
      return props.modelValue;
    },
    set(value: TransferModelValue) {
      emit('update:modelValue', value);
    },
  });

  const directions = [
    { direction: Direction.There, title: DIRECTION_TITLES[Direction.There] },
    { direction: Direction.Back, title: DIRECTION_TITLES[Direction.Back] },
  ];

  const onChange = (value: DirectionModelValue, direction: Direction) => {
    innerValue.value = { ...innerValue.value, [direction]: value };
  };
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <Transition name="fade" mode="out-in">
    <div :key="loading" class="tl-transfer">
      <template v-if="loading">
        <DirectionSkeleton v-for="item in directions" :key="item.direction" :title="item.title" />
        <ResultSkeleton />
      </template>

      <template v-else>
        <TransferDirection
          v-for="item in directions"
          :key="item.direction"
          :model-value="innerValue[item.direction]"
          :title="item.title"
          :direction="item.direction"
          :passengers="passengers"
          :points="points[item.direction]"
          :types="types[item.direction]"
          :options="options[item.direction]"
          @update:modelValue="onChange($event, item.direction)"
        />

        <TransferResult :model-value="innerValue">
          <slot />
        </TransferResult>
      </template>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
  .tl-transfer {
    background-color: var(--color-bg-primary);
    border-radius: var(--rounding-xl);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-m);
    padding: var(--spacing-l);

    @media (max-width: $breakpoint-mobile-max) {
      background-color: var(--color-neutral-05);
    }
  }
</style>
